// Nightly EOD numbers: types, defaults and the maths (mirrors the venue's Excel EOD sheet).

export const DENOMS: Array<{ key: string; label: string; value: number }> = [
  { key: '100', label: '$100', value: 100 }, { key: '50', label: '$50', value: 50 }, { key: '20', label: '$20', value: 20 },
  { key: '10', label: '$10', value: 10 }, { key: '5', label: '$5', value: 5 }, { key: '2', label: '$2', value: 2 },
  { key: '1', label: '$1', value: 1 }, { key: '0.5', label: '50c', value: 0.5 }, { key: '0.2', label: '20c', value: 0.2 },
  { key: '0.1', label: '10c', value: 0.1 }, { key: '0.05', label: '5c', value: 0.05 },
];

export interface EodConfig {
  /** Till float. null = not set yet (the manager enters it). */
  float: number | null;
  /** Services in order. Every service except the last gets an X-read photo; the last one is the close. */
  services: string[];
  /** Sales tax rate used only when the printout has no ex-tax figure (0.1 = 10% GST). */
  taxRate: number;
  discountCodes: string[];
  debtorNames: string[];
  offers: string[];
  /** Deputy area names containing any of these count as kitchen (BOH); everything else is FOH. */
  bohKeywords: string[];
  /** Hourly rates used for shifts that have no pay rate in Deputy. null = don't estimate. */
  fohRate?: number | null;
  bohRate?: number | null;
  /** People the manager has moved by hand (user ids), overriding area and position. */
  bohUserIds?: string[];
  fohUserIds?: string[];
  // ---- Report setup (all optional: older settings keep working) ----
  /** Which template the venue started from (only for showing it in setup). */
  template?: string;
  /** Sections switched on or off. Missing = the default in EOD_SECTIONS. */
  sections?: Partial<Record<EodSectionId, boolean>>;
  /** Card types surcharges are recorded for. */
  surchargeTypes?: string[];
  /** The group surcharge is paid out to staff, so it counts as tips. */
  groupSurchargeIsTip?: boolean;
  /** Targets used to flag the reports (percent numbers, e.g. 32.9). */
  targets?: { labourPct?: number | null; fohPct?: number | null; bohPct?: number | null; bevPct?: number | null };
  /** Service summary: stations on the floor and in the kitchen, and the topics the manager writes about. */
  stationsFront?: string[];
  stationsKitchen?: string[];
  noteTopics?: string[];
}

export type EodSectionId = 'services' | 'cash' | 'payments' | 'surcharges' | 'tips' | 'debtors' | 'discounts' | 'offers' | 'labour' | 'serviceSummary';
export const EOD_SECTIONS: Array<{ id: EodSectionId; label: string; hint: string; on: boolean; locked?: boolean }> = [
  { id: 'services', label: 'Sales by service', hint: 'Split sales, covers and food/drinks by service (needs an X-read photo per service).', on: true },
  { id: 'cash', label: 'Cash count', hint: 'Count the till by note and coin, check it against the POS.', on: true },
  { id: 'payments', label: 'Payments check', hint: 'Cash, cards and the rest must add up to sales.', on: true },
  { id: 'surcharges', label: 'Surcharges', hint: 'Surcharges by card type, for reference.', on: false },
  { id: 'tips', label: 'Tips', hint: 'Card tips, cash tips and the group surcharge, for paying out.', on: true },
  { id: 'debtors', label: 'Accounts (on account)', hint: 'Bills put on account, e.g. directors.', on: true },
  { id: 'discounts', label: 'Discounts, deleted & refunds', hint: 'Every discount with who and why.', on: true },
  { id: 'offers', label: 'Offers', hint: 'Uses and value of current promotions.', on: true },
  { id: 'labour', label: 'Labour', hint: 'Hours and wages from Deputy, front and kitchen.', on: true },
  { id: 'serviceSummary', label: 'Service summary', hint: 'Who was on which station, and notes on each service.', on: false },
];
export const sectionOn = (cfg: EodConfig, id: EodSectionId): boolean => cfg.sections?.[id] ?? EOD_SECTIONS.find((x) => x.id === id)?.on ?? true;

export const DEFAULT_SURCHARGE_TYPES = ['Amex', 'Mastercard', 'Visa', 'EFTPOS', 'Public holiday'];
export const DEFAULT_STATIONS_FRONT = ['MOD', 'OUT 1', 'OUT 2', 'OUT 3', 'IN 1', 'IN 2', 'RUN', 'SOM', 'Bar 1', 'Bar 2'];
export const DEFAULT_STATIONS_KITCHEN = ['MOD', 'Hot line', 'Larder', 'Grill', 'Fry', 'Kitchen hand'];
export const DEFAULT_NOTE_TOPICS = ['Guest experience', 'Guest issues / complaints', 'Going above and beyond', 'Kitchen', 'Bar', 'Training'];

/** Starter setups by kind of venue. Applying one only fills settings; everything stays editable. */
export const EOD_TEMPLATES: Array<{ id: string; label: string; hint: string; config: Partial<EodConfig> }> = [
  { id: 'restaurant', label: 'Restaurant', hint: 'Lunch and dinner, full cash and payments checks.', config: {
    services: ['Lunch', 'Dinner'],
    sections: { services: true, cash: true, payments: true, surcharges: false, tips: true, debtors: true, discounts: true, offers: true, labour: true, serviceSummary: true },
    noteTopics: ['Guest experience', 'Guest issues / complaints', 'Going above and beyond', 'Kitchen', 'Bar', 'Wine', 'Training'],
    stationsFront: ['MOD', 'OUT 1', 'OUT 2', 'OUT 3', 'IN 1', 'RUN', 'SOM', 'Bar'], stationsKitchen: ['MOD', 'Hot line', 'Larder', 'Grill', 'Kitchen hand'],
  } },
  { id: 'restaurant_hh', label: 'Restaurant with happy hour', hint: 'Lunch, happy hour and dinner.', config: {
    services: ['Lunch', 'Happy hour', 'Dinner'],
    sections: { services: true, cash: true, payments: true, surcharges: true, tips: true, debtors: true, discounts: true, offers: true, labour: true, serviceSummary: true },
    noteTopics: ['Guest experience', 'Guest issues / complaints', 'Going above and beyond', 'Kitchen', 'Bar', 'Wine', 'Training', 'Happy hour / events'],
  } },
  { id: 'cafe', label: 'Café', hint: 'One trading period, simple checks.', config: {
    services: ['Day'],
    sections: { services: false, cash: true, payments: true, surcharges: false, tips: true, debtors: false, discounts: true, offers: false, labour: true, serviceSummary: false },
    discountCodes: ['Staff', 'Comp', 'Loyalty', 'Other'],
  } },
  { id: 'bar', label: 'Bar', hint: 'Evening trade, tips and cash focus.', config: {
    services: ['Evening'],
    sections: { services: false, cash: true, payments: true, surcharges: true, tips: true, debtors: false, discounts: true, offers: true, labour: true, serviceSummary: true },
    stationsFront: ['MOD', 'Bar 1', 'Bar 2', 'Floor', 'Door'], stationsKitchen: ['Kitchen'],
    noteTopics: ['Guest experience', 'Incidents', 'Bar', 'Stock'],
  } },
];

// Generic defaults. Each venue sets its own in the EOD settings (float, services, discount codes, accounts, offers).
export const DEFAULT_EOD_CONFIG: EodConfig = {
  float: null,
  services: ['Lunch', 'Dinner'],
  taxRate: 0.1,
  discountCodes: ['Guest experience / comp', 'Manager discretion', 'Complaint', 'Staff error', 'Manager meal', 'Staff discount', 'Other'],
  debtorNames: [],
  offers: [],
  bohKeywords: ['kitchen', 'chef', 'boh', 'cook', 'kp', 'dish', 'larder', 'grill', 'pastry', 'hot line', 'fry', 'prep'],
};

/** Figures read from an OrderMate "Takings - All Terminals" print (close or X-read). */
export interface Printout {
  trading_date?: string | null; printed_time?: string | null;
  total_sales?: number | null; food_sales?: number | null; beverage_sales?: number | null; other_sales?: number | null;
  covers?: number | null; gst?: number | null; sales_ex_gst?: number | null;
  cash?: number | null; visa?: number | null; mastercard?: number | null; amex?: number | null; diners?: number | null;
  jcb?: number | null; alipay?: number | null; bankcard?: number | null; eftpos_savings?: number | null; eftpos_cheque?: number | null;
  all_cards_total?: number | null; all_cards_total_inc_tips?: number | null;
  internet_prepayment?: number | null; sevenrooms?: number | null; voucher_redeems?: number | null; debtor_sales?: number | null;
  cash_tips?: number | null; card_tips?: number | null; web_tips?: number | null;
  group_surcharge?: number | null; other_surcharges?: number | null;
  deleted_items?: number | null; refunded_items?: number | null; discounts_total?: number | null;
  discount_lines?: Array<{ type: string; amount: number | null; times: number | null }>; open_accounts_total?: number | null;
  /** Per-service figures, when the image splits them (e.g. the venue's own end-of-day sheet). */
  services?: Array<{ name: string; total_sales?: number | null; food_sales?: number | null; beverage_sales?: number | null; covers?: number | null }>;
  unreadable?: string[];
}

export interface EodRow { code: string; amount: number | null; detail: string }
/** One service in the service summary: who was on which station, and notes by topic. */
export interface ServiceNotes { stations: Record<string, string>; notes: Record<string, string> }
export interface DebtorRow { name: string; amount: number | null; detail: string }
export interface OfferRow { name: string; uses: number | null; value: number | null; note: string }

export interface EodData {
  manager: string;
  close: Printout | null;
  /** X-read printouts keyed by service name (every service except the last). */
  xreads: Record<string, Printout | null>;
  /** Manager overrides; null = use the close printout. */
  sales: { total: number | null; food: number | null; bev: number | null; covers: number | null };
  cash: { float: number | null; openedBy: string; counts: Record<string, number | null>; countedBy: string; note: string; noCash?: boolean };
  payments: { deposits: number | null; note: string };
  tips: { cash: number | null };
  discounts: EodRow[];
  deleted: { amount: number | null; detail: string };
  refunds: { amount: number | null; detail: string };
  debtors: DebtorRow[];
  labour: { fohHours: number | null; fohCost: number | null; bohHours: number | null; bohCost: number | null; source: 'roster' | 'manual' | null };
  offers: OfferRow[];
  notes: string;
  /** Surcharges by card type (when that section is on). */
  surcharges?: Record<string, number | null>;
  /** Service summary per service name (when that section is on). */
  service?: Record<string, ServiceNotes>;
  /** Covers typed in for a service when a printout didn't show them (e.g. the photo cut off the covers line). */
  serviceCovers?: Record<string, number | null>;
  /** Sales typed in (or read from a sheet) for a service, when there's no X-read for it. That service's own sales, not a running total. */
  serviceSales?: Record<string, number | null>;
}

export function emptyEod(cfg: EodConfig, manager: string): EodData {
  return {
    manager, close: null, xreads: {},
    sales: { total: null, food: null, bev: null, covers: null },
    cash: { float: cfg.float, openedBy: manager, counts: {}, countedBy: manager, note: '' },
    payments: { deposits: null, note: '' },
    tips: { cash: null },
    discounts: [],
    deleted: { amount: null, detail: '' },
    refunds: { amount: null, detail: '' },
    debtors: cfg.debtorNames.map((name) => ({ name, amount: null, detail: '' })),
    labour: { fohHours: null, fohCost: null, bohHours: null, bohCost: null, source: null },
    offers: cfg.offers.map((name) => ({ name, uses: null, value: null, note: '' })),
    notes: '',
  };
}

const r2 = (n: number) => Math.round(n * 100) / 100;
const v = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? 0 : n);
const sum = (xs: Array<number | null | undefined>) => r2(xs.reduce<number>((a, b) => a + v(b), 0));

export interface ServiceSplit { sales: number | null; covers: number | null; perHead: number | null; food: number | null; bev: number | null }

export function computeEod(d: EodData, cfg: EodConfig) {
  const c = d.close ?? {};
  const total = d.sales.total ?? c.total_sales ?? null;
  const food = d.sales.food ?? c.food_sales ?? null;
  const bev = d.sales.bev ?? c.beverage_sales ?? (total != null && food != null ? r2(total - food - v(c.other_sales)) : null);
  const covers = d.sales.covers ?? c.covers ?? null;
  const exGst = c.sales_ex_gst ?? (total != null ? r2(total / (1 + (cfg.taxRate || 0))) : null);
  const perHead = total != null && covers ? r2(total / covers) : null;

  // Service split from X-reads (cumulative running totals through the day):
  // each service = its X-read minus the previous one; the last service = close minus the last X-read.
  const split = (s: number | null, cv: number | null, f: number | null, b: number | null): ServiceSplit => ({ sales: s, covers: cv, perHead: s != null && cv ? r2(s / cv) : null, food: f, bev: b });
  const services: Record<string, ServiceSplit> = {};
  const names = cfg.services.length ? cfg.services : ['Day'];
  let prevSales: number | null = 0, prevCovers: number | null = 0, prevFood: number | null = 0, prevBev: number | null = 0;
  const diff = (cur: number | null, prev: number | null) => (cur != null && prev != null ? r2(cur - prev) : null);
  names.forEach((name, i) => {
    const last = i === names.length - 1;
    const x = d.xreads[name];
    let cumSales = last ? total : x?.total_sales ?? null;
    let cumCovers = last ? covers : x?.covers ?? null;
    const cumFood = last ? food : x?.food_sales ?? null;
    const cumBev = last ? bev : x?.beverage_sales ?? (x?.total_sales != null && x?.food_sales != null ? r2(x.total_sales - x.food_sales - v(x.other_sales)) : null);
    let svcCovers = cumCovers != null && prevCovers != null ? cumCovers - prevCovers : null;
    const typed = d.serviceCovers?.[name];
    if (typed != null) {
      svcCovers = typed;
      // The running total wasn't readable: carry on from what was typed, so the next service still works out.
      if (cumCovers == null && prevCovers != null) cumCovers = prevCovers + typed;
    }
    let svcSales = diff(cumSales, prevSales);
    let svcFood = diff(cumFood, prevFood), svcBev = diff(cumBev, prevBev);
    const typedSales = d.serviceSales?.[name];
    if (typedSales != null) {
      svcSales = typedSales;
      if (!x || x.total_sales == null) { svcFood = null; svcBev = null; }
      // No running total for this service: carry on from what was typed.
      if (!last && (x?.total_sales == null) && prevSales != null) cumSales = r2(prevSales + typedSales);
    }
    services[name] = split(svcSales, svcCovers, svcFood, svcBev);
    prevSales = cumSales; prevCovers = cumCovers; prevFood = cumFood; prevBev = cumBev;
  });

  // Cash: counted − float = takings; must match OrderMate's cash.
  // "No cash taken" = the till holds exactly the float, so no counting needed.
  const counted = d.cash.noCash ? v(d.cash.float) : r2(Object.entries(d.cash.counts).reduce((a, [k, n]) => a + Number(k) * v(n), 0));
  const anyCounted = !!d.cash.noCash || Object.values(d.cash.counts).some((n) => n != null);
  const cashTakings = anyCounted ? r2(counted - v(d.cash.float)) : null;
  const systemCash = c.cash ?? null;
  const cashVariance = cashTakings != null ? r2(cashTakings - v(systemCash)) : null;

  // Payments: cash + cards (excl. tips) + deposits + vouchers + online = sales.
  const cardsIncTips = c.all_cards_total_inc_tips ?? (c.all_cards_total != null ? r2(c.all_cards_total + v(c.card_tips)) : null);
  const cardsExTips = cardsIncTips != null ? r2(cardsIncTips - v(c.card_tips)) : null;
  const online = sum([c.internet_prepayment, c.sevenrooms]);
  const vouchers = c.voucher_redeems ?? 0;
  const paymentsTotal = sum([cashTakings ?? systemCash, cardsExTips, d.payments.deposits, vouchers, online]);
  const paymentsVariance = total != null && (cardsIncTips != null || cashTakings != null) ? r2(paymentsTotal - total) : null;

  // The group surcharge is paid to staff at some venues: then it counts as tips, like the venue's sheet.
  const groupAsTip = !!cfg.groupSurchargeIsTip;
  const tipsTotal = sum([c.card_tips, c.web_tips, d.tips.cash, groupAsTip ? c.group_surcharge : 0]);
  const surchargesByType = sum(Object.values(d.surcharges ?? {}));
  const surchargesTotal = surchargesByType || sum([groupAsTip ? 0 : c.group_surcharge, c.other_surcharges]);
  const discountsTotal = sum(d.discounts.map((x) => x.amount));
  const deletedAmount = d.deleted.amount ?? c.deleted_items ?? null;
  const labourTotal = d.labour.fohCost != null || d.labour.bohCost != null ? sum([d.labour.fohCost, d.labour.bohCost]) : null;
  const labourPct = labourTotal != null && exGst ? Math.round((labourTotal / exGst) * 1000) / 10 : null;

  return {
    total, food, bev, covers, exGst, perHead,
    foodPct: total && food != null ? Math.round((food / total) * 1000) / 10 : null,
    services,
    counted, cashTakings, systemCash, cashVariance,
    cardsIncTips, cardTips: c.card_tips ?? null, cardsExTips, online, vouchers, paymentsTotal, paymentsVariance,
    tipsTotal, surchargesTotal, groupSurcharge: c.group_surcharge ?? null, discountsTotal, deletedAmount, debtorsTotal: sum(d.debtors.map((x) => x.amount)),
    labourTotal, labourHours: sum([d.labour.fohHours, d.labour.bohHours]), labourPct,
  };
}

export type EodCalc = ReturnType<typeof computeEod>;

/** What must be right before the report can be submitted. */
export function eodChecks(d: EodData, k: EodCalc, cfg?: EodConfig): Array<{ ok: boolean; label: string }> {
  const zero = (x: number | null) => x != null && Math.abs(x) < 0.01;
  const on = (id: EodSectionId) => !cfg || sectionOn(cfg, id);
  return [
    { ok: k.total != null, label: 'Total sales entered (photo of the end-of-day print)' },
    { ok: k.covers != null, label: 'Covers entered' },
    ...(on('cash') ? [
      { ok: k.cashTakings != null, label: 'Cash counted' },
      { ok: zero(k.cashVariance) || d.cash.note.trim().length > 3, label: 'Cash variance is zero, or explained' },
    ] : []),
    ...(on('payments') ? [{ ok: zero(k.paymentsVariance) || d.payments.note.trim().length > 3, label: 'Payments variance is zero, or explained' }] : []),
    ...(on('discounts') ? [
      { ok: d.discounts.every((x) => !x.amount || x.detail.trim().length > 2), label: 'Every discount has a name and reason' },
      ...(d.close?.discounts_total ? [{ ok: Math.abs(k.discountsTotal - (d.close?.discounts_total ?? 0)) < 0.5, label: `Discounts entered match the printout (${d.close?.discounts_total.toFixed(2)})` }] : []),
      { ok: !v(k.deletedAmount) || d.deleted.detail.trim().length > 2, label: 'Deleted items explained' },
    ] : []),
  ];
}
